import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import {
  getX, hasDateValues, daysToDate, smartYMin,
  renderXAxisLabel, renderYAxisLabel, yLabelNeedsWrap,
  truncateLabel, xTickHeight, xTicksNeedRotation, tickLabelLines,
  formatChartNumber, xLabelDisplay,
} from './chartUtils';
import { ensureTooltip } from './chartTooltip';

function BarChartRenderer({ data, width, height, xLabel, yLabel }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0 || !width || !height) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const isDate = hasDateValues(data);
    const baseLeft = isDate ? 110 : 70;
    const fontSize = 11;
    const charWidth = fontSize * 0.55;
    const rawLabels = data.map((d) => String(getX(d)));
    // ISO date buckets read as "Apr 2026"; any other label is shown as sent.
    const showX = xLabelDisplay(rawLabels);
    const shownLabels = rawLabels.map(showX);
    const estBand = (width - baseLeft - 20) / Math.max(data.length, 1);
    const maxCharsPerLine = Math.max(8, Math.floor(estBand * 0.9 / charWidth));
    const rotateX = !isDate && xTicksNeedRotation(shownLabels, estBand);

    // Per-tick lines: rotated → 1 truncated; horizontal → 1 or 2 wrapped
    const xLines = rotateX
      ? shownLabels.map((l) => [truncateLabel(l)])
      : isDate
        ? shownLabels.map((l) => [l])
        : shownLabels.map((l) => tickLabelLines(l, maxCharsPerLine));
    const linesByLabel = new Map(rawLabels.map((l, i) => [l, xLines[i]]));

    const tickH = rotateX
      ? xTickHeight(xLines.map((ls) => ls[0]), fontSize, true)
      : fontSize + 4 + (Math.max(1, ...xLines.map((ls) => ls.length)) - 1) * 14;
    const marginBottom = Math.max(60, tickH + (xLabel ? 24 : 8));
    const wrapsY = yLabelNeedsWrap(yLabel, height - 20 - marginBottom);
    const margin = { top: 20, right: 20, bottom: marginBottom, left: baseLeft + (wrapsY ? 14 : 0) };
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;
    const g = svg.attr('width', width).attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const x = d3.scaleBand().domain(data.map((d) => getX(d))).range([0, w]).padding(0.3);
    const yMin = isDate ? d3.min(data, (d) => d.value) * 0.95 : smartYMin(data);
    const yMax = d3.max(data, (d) => d.value) * 1.05;
    const y = d3.scaleLinear().domain([yMin, yMax]).range([h, 0]);

    const xTickText = g.append('g').attr('transform', `translate(0,${h})`)
      .call(d3.axisBottom(x).tickFormat(showX))
      .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif');
    if (rotateX) {
      xTickText
        .text((d) => (linesByLabel.get(String(d)) || [String(d)])[0])
        .attr('transform', 'rotate(-45)').attr('text-anchor', 'end')
        .attr('dx', '-0.3em').attr('dy', '0.3em');
    } else {
      xTickText.each(function (d) {
        const t = d3.select(this);
        const lines = linesByLabel.get(String(d)) || [String(d)];
        t.text(null).attr('dy', null);
        lines.forEach((line, i) => {
          t.append('tspan').attr('x', 0).attr('dy', i === 0 ? '0.71em' : '1.1em').text(line);
        });
      });
    }
    xTickText.append('title').text(showX);

    const yAxisFormat = isDate ? (v) => daysToDate(v) : formatChartNumber;
    const yAxis = d3.axisLeft(y).tickFormat(yAxisFormat);
    // Integer counts with a small max: use integer tick values so we don't render
    // 5 fractional ticks that round to duplicate labels (e.g. "1,1,1,0,0,0").
    const yMaxVal = d3.max(data, (d) => d.value) || 0;
    const allInt = data.every((d) => Number.isInteger(d.value));
    if (!isDate && allInt && yMaxVal <= 10) {
      yAxis.tickValues(d3.range(0, Math.max(1, yMaxVal) + 1));
    } else {
      yAxis.ticks(5);
    }
    g.append('g').call(yAxis)
      .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif');

    // A plain bar and a histogram both show ONE variable, so every bar/bin uses a
    // single consistent colour — a per-bar rainbow implied categories that aren't
    // there (PRD-1370 #19). A per-datum `color` override still wins when supplied.
    const BAR_COLOR = '#4A90D9';
    // PRD-1370 #20: an interactive tooltip on the bar (a big hover target) so a
    // truncated x-label is always readable — the native <title> was unreliable.
    const tip = ensureTooltip(svgRef.current && svgRef.current.parentNode);
    const tipText = (d) => `${showX(getX(d))}: ${isDate ? daysToDate(d.value) : formatChartNumber(d.value)}`;
    g.selectAll('.bar').data(data).enter().append('rect')
      .attr('x', (d) => x(getX(d)))
      .attr('y', (d) => y(d.value))
      .attr('width', x.bandwidth())
      .attr('height', (d) => y(yMin) - y(d.value))
      .attr('fill', (d) => d.color || BAR_COLOR)
      .attr('rx', 2)
      .style('cursor', 'default')
      .on('mousemove', (event, d) => tip.show(tipText(d), event))
      .on('mouseleave', () => tip.hide());

    // Value labels on top of each bar — show date when value is a date
    const labelFormat = isDate ? (v) => daysToDate(v) : formatChartNumber;
    g.selectAll('.bar-label').data(data).enter().append('text')
      .attr('x', (d) => x(getX(d)) + x.bandwidth() / 2)
      .attr('y', (d) => y(d.value) - 5)
      .attr('text-anchor', 'middle').attr('font-size', '11px')
      .attr('font-family', 'Roboto, sans-serif').attr('fill', '#212121')
      .text((d) => labelFormat(d.value));

    // Axis labels from AI
    renderXAxisLabel(g, xLabel, w, h, tickH);
    renderYAxisLabel(g, yLabel, h, margin.left);
  }, [data, width, height, xLabel, yLabel]);

  return <svg ref={svgRef} />;
}

export default BarChartRenderer;
